import React, { FC, memo, useCallback } from 'react';
import classNames from 'classnames';
import Checkbox from '@zvuk-b2b/react-uikit/ui/Checkbox';
import { type ListChildComponentProps } from 'react-window';

import { PlayButton } from 'application/components/PlayButton';

import { DATA_TEST_IDS } from '../../consts';
import { TEXTS } from '../../texts';
import PromoListSkeleton from '../PromoListSkeleton';
import type { PromoListItemData } from '../types';
import { PROMO_STATUS_TITLE_BY_STATUS } from './consts';
import PromoOptionProgress from './PromoOptionProgress';

import styles from './index.module.scss';

const PromoOption: FC<ListChildComponentProps<PromoListItemData>> = ({ data, index, style }) => {
  const { fileUuid, isLoading, isPaused, pause, resume } = data;
  const promo = data.items[index];

  const onPlaybackToggle = useCallback(() => {
    if (!promo) {
      return;
    }

    const isCurrentPromo = fileUuid === promo.id;

    if (isCurrentPromo && !isPaused) {
      pause();

      return;
    }

    if (isCurrentPromo) {
      resume();

      return;
    }

    data.onPromoPlaybackToggle(promo);
  }, [data, fileUuid, isPaused, pause, promo, resume]);

  if (!promo) {
    return (
      <PromoListSkeleton
        dataTestId={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_NEXT_PAGE_SKELETON}
        rowsCount={1}
        style={style}
      />
    );
  }

  const isCurrentPromo = fileUuid === promo.id;
  const isSelected = promo.isSelected;
  const isPlaying = isCurrentPromo && !isPaused;
  const statusTitle = PROMO_STATUS_TITLE_BY_STATUS[promo.status];

  return (
    <div
      className={styles.row}
      style={style}>
      <Checkbox
        elementId={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION_CHECKBOX(promo.id)}
        buttonClassName={styles.checkboxButton}
        isDisabled={data.isBulkSelectionActionPending}
        isChecked={isSelected}
        onChange={(isChecked) => data.onPromoSelectionChange(promo, isChecked)}
      />
      <div
        className={classNames(styles.option, {
          [styles.optionActive]: isCurrentPromo,
        })}
        data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION(promo.id)}>
        <button
          type="button"
          className={styles.playButton}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION_PLAY(promo.id)}
          aria-label={
            isPlaying ? TEXTS.AUDIO_REPORT_PROMOS_PAUSE(promo.title) : TEXTS.AUDIO_REPORT_PROMOS_PLAY(promo.title)
          }
          disabled={!promo.audioUrl}
          title={!promo.audioUrl ? TEXTS.AUDIO_REPORT_PROMOS_DATA_ERROR : undefined}
          onClick={onPlaybackToggle}>
          <PlayButton
            isLoading={isCurrentPromo && isLoading}
            isMediaFilePaused={isPaused}
            isMediafilePlaying={isCurrentPromo}
          />
        </button>
        <div className={styles.content}>
          <div
            className={styles.title}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION_TITLE(promo.id)}>
            {promo.title}
          </div>
          <div
            className={styles.campaign}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION_CAMPAIGN(promo.id)}>
            {promo.campaignTitle}
          </div>
        </div>
        <div
          className={styles.status}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_OPTION_STATUS(promo.id)}>
          <span
            className={classNames(styles.statusDot, {
              [styles.statusDotActive]: promo.status === 'active',
              [styles.statusDotPassed]: promo.status === 'passed',
              [styles.statusDotPlanned]: promo.status === 'planned',
              [styles.statusDotStopped]: promo.status === 'stopped',
            })}
          />
          <span>{statusTitle}</span>
        </div>
        <PromoOptionProgress promoId={promo.id} />
      </div>
    </div>
  );
};

export default memo(PromoOption);
